import { AxiosRequestConfig } from "axios";
import apiInstance from "./instance";
import endpoints from "./endpoints";

export interface ApiEnvelope<T> {
  data?: T;
  status_code?: number;
  status_message?: string;
  response_error?: boolean;
}

export interface CasinoAnalysisEvent {
  provider_event_id: string;
  provider_game_id?: string;
  provider_name?: string;
  event_name?: string;
  total_order?: number;
  total_order_count?: number;
  exposure?: number;
  total_amount?: number;
  max_profit?: number;
  [key: string]: unknown;
}

interface LiveCasinoEventsData {
  liveCasinoEvents?: CasinoAnalysisEvent[];
  virtualCasinoEvents?: CasinoAnalysisEvent[];
}

export interface CasinoEventsResponse {
  liveCasinoEvents: CasinoAnalysisEvent[];
  virtualCasinoEvents: CasinoAnalysisEvent[];
}

export interface CasinoUrlPayload {
  gameId: string;
  provider_type: string;
  providerName: string;
  is_mobile: boolean;
  clientIp: string;
  redirectUrl: string;
}

export interface CasinoUrlResponseData {
  url?: string;
  [key: string]: unknown;
}

export interface CasinoOpenBet {
  user_name?: string;
  userName?: string;
  market_name?: string;
  market?: string;
  runner_name?: string;
  runner?: string;
  odds?: number | string;
  rate?: number | string;
  bet_amount?: number | string;
  amount?: number | string;
  [key: string]: unknown;
}

const getErrorMessage = (fallback: string, message?: string) => message || fallback;
const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;
const isApiEnvelope = <T>(value: unknown): value is ApiEnvelope<T> =>
  isObject(value) && ("response_error" in value || "status_message" in value || "data" in value);

export const casinoAnalysisApi = {
  async fetchLiveCasinoEvents(config?: AxiosRequestConfig): Promise<CasinoEventsResponse> {
    const { data } = await apiInstance.get<ApiEnvelope<LiveCasinoEventsData> | LiveCasinoEventsData | CasinoAnalysisEvent[]>(
      endpoints.casino.liveCasinoEvents,
      config
    );

    if (Array.isArray(data)) {
      return {
        liveCasinoEvents: data,
        virtualCasinoEvents: [],
      };
    }

    if (isApiEnvelope<LiveCasinoEventsData>(data) && data.response_error) {
      throw new Error(getErrorMessage("Failed to load casino events.", data.status_message));
    }

    const payload: LiveCasinoEventsData = isApiEnvelope<LiveCasinoEventsData>(data)
      ? data.data || {}
      : (data as LiveCasinoEventsData);
    return {
      liveCasinoEvents: Array.isArray(payload?.liveCasinoEvents) ? payload.liveCasinoEvents : [],
      virtualCasinoEvents: Array.isArray(payload?.virtualCasinoEvents) ? payload.virtualCasinoEvents : [],
    };
  },

  async fetchCasinoUrl(payload: CasinoUrlPayload, config?: AxiosRequestConfig): Promise<CasinoUrlResponseData> {
    const { data } = await apiInstance.post<ApiEnvelope<CasinoUrlResponseData> | CasinoUrlResponseData>(
      endpoints.casino.casinoUrl,
      payload,
      config
    );

    if (isApiEnvelope<CasinoUrlResponseData>(data) && data.response_error) {
      throw new Error(getErrorMessage("Failed to load casino URL.", data.status_message));
    }

    return isApiEnvelope<CasinoUrlResponseData>(data)
      ? data.data || {}
      : (data as CasinoUrlResponseData);
  },

  async fetchCasinoOpenBets(providerEventId: string, config?: AxiosRequestConfig): Promise<CasinoOpenBet[]> {
    const { data } = await apiInstance.get<ApiEnvelope<CasinoOpenBet[]> | CasinoOpenBet[]>(
      endpoints.casino.openBet(providerEventId),
      config
    );

    if (Array.isArray(data)) {
      return data;
    }

    if (isApiEnvelope<CasinoOpenBet[]>(data) && data.response_error) {
      throw new Error(getErrorMessage("Failed to load open bets.", data.status_message));
    }

    return isApiEnvelope<CasinoOpenBet[]>(data) && Array.isArray(data.data) ? data.data : [];
  },
};
